import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ShoppingCart, Package, FileText, Truck, CheckCircle2, AlertTriangle,
  Plus, Search, X, Loader2, Building2, ClipboardList, Receipt,
  ArrowRight, BadgeCheck, Clock, DollarSign, TrendingUp, Banknote,
  ChevronDown, ChevronRight, Eye, BarChart2, MapPin, Filter,
  Check, RotateCcw, Calendar, AlertCircle, Boxes, MessageSquare,
  Download, Printer, History, FileCheck, Pencil, Paperclip, UploadCloud,
  List, BookOpen, Tag, ExternalLink, Brain, Sparkles, FileUp, CheckCheck, Zap
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG, formatPYGCompact, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { Modal, StatCard, StatusBadge, StatusTimeline, PedidoDetailModal, AprobacionParcial, mensajeAprobacion, descargarPedidoPDF, presupuestoRef, textoPresupuestoRef } from "./SharedComponents";
import { FLOW_STEPS, STATUS_COLORS, STATUS_LABELS, UNITS, WORK_FRONTS, URGENCY_LABELS } from "./constants";
import { formatDateTime, buildHistory, generatePedidoPDF, generatePPPDF, generateAPPDF } from "./helpers";
import { FiltroClienteObraPresupuesto, matchClienteObraPresupuesto } from "./FiltroClienteObraPresupuesto";
import { BusquedaEstadoUrgenciaFilters } from "./BusquedaFilters";
import { roleAllowed } from "../../utils/roles";
import { useAprobadorPedidos } from "./RangosAprobacionTab";   // Fix EX
import { AutocompleteInput } from "../../components/AutocompleteInput";   // Fix IU

export function AprobacionesTab({
  requests, loading, onRefresh, userRole,
  listasClientes, listasObras, allBudgets,
  filterCliente, setFilterCliente, filterObra, setFilterObra, filterPresupuesto, setFilterPresupuesto,
}) {
  const [actionModal, setActionModal] = useState(null); // { type: 'rech'|'mod'|'aprob', request }
  const [comment, setComment] = useState("");
  const [processing, setProcessing] = useState(null);
  const [viewRequest, setViewRequest] = useState(null);
  // BLOQUE 50 PARTE A — Buscar + Estado + Urgencia (además de Cliente/Obra/Presupuesto)
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterUrgency, setFilterUrgency] = useState("");

  // Edit modal state (coordinator can directly modify items)
  const [editModal, setEditModal] = useState(null); // { request }
  const [editForm, setEditForm] = useState({ items: [], urgency: "normal", target_date: "", sector: "", notes: "", comment: "" });
  const [editSaving, setEditSaving] = useState(false);
  // Fix AS (2026-09-05): "yo le doy editar pedido y tampoco me da la opción de
  // imputar a item" (Edson, 05/09). El modal de edición ahora trae las líneas del
  // presupuesto vinculado para poder imputar (o corregir) cada material antes de
  // aprobar. Sin imputación, el consumo de la obra no cuenta ese material.
  const [editItemsPto, setEditItemsPto] = useState([]);
  // Fix DD (2026-09-16): "cuando le doy editar a un pedido sale así… acá no sale nivel,
  // sale sector. Tendría que estar la columna de nivel y tendría que ser en formato de
  // despliegue y que las opciones estén según como se cargó en presupuesto o en obra"
  // (Edson, 16/09). Los niveles vienen del mismo endpoint que ya se pedía para los ítems:
  // son los que se cargaron en el presupuesto, que es de donde los toma la obra.
  const [editNiveles, setEditNiveles] = useState([]);
  const [editCatalogoTareas, setEditCatalogoTareas] = useState([]);
  const [editPtoInfo, setEditPtoInfo] = useState(null);   // Fix ED (2026-09-24)

  useEffect(() => {
    const ptoId = editModal?.request?.presupuesto_id;
    if (!ptoId) { setEditItemsPto([]); setEditNiveles([]); setEditPtoInfo(null); return; }
    axios.get(`${API}/budgets/${ptoId}/items-para-pedido`, { withCredentials: true })
      .then(({ data }) => { setEditItemsPto(data?.items || []); setEditNiveles(data?.niveles || []); setEditPtoInfo(data?.presupuesto || null); })
      .catch(() => { setEditItemsPto([]); setEditNiveles([]); setEditPtoInfo(null); });
  }, [editModal]);

  // Fix DD (2026-09-16): el mismo catálogo de tareas que ofrece el formulario de carga.
  useEffect(() => {
    if (!editModal) return;
    axios.get(`${API}/catalogo-tareas`, { withCredentials: true })
      .then(({ data }) => setEditCatalogoTareas(Array.isArray(data) ? data : []))
      .catch(() => setEditCatalogoTareas([]));
  }, [editModal]);

  // Nombre de cada tarea y, por ítem del presupuesto, las que de verdad le corresponden.
  const editTareas = useMemo(() => {
    const nombres = {};
    editCatalogoTareas.forEach(t => { nombres[t.numero] = t.nombre; });
    const porItem = {};
    editItemsPto.forEach(bi => {
      const nums = (bi.tareas || []).map(t => t.tarea_numero).filter(n => n != null);
      if (nums.length > 0) porItem[bi.item_uid] = nums;
    });
    return { todas: editCatalogoTareas, nombres, porItem };
  }, [editCatalogoTareas, editItemsPto]);

  const canApprove = roleAllowed(userRole, ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Compras"]);
  const aprobadorPerfil = useAprobadorPedidos();   // Fix EX (2026-10-05)

  const filterCriteria = { filterCliente, filterObra, filterPresupuesto };
  const filterCatalogos = { listasClientes, listasObras };
  const baseFiltered = requests.filter(r => matchClienteObraPresupuesto(r, filterCriteria, filterCatalogos));
  const matchSearch = (r) => !search || (r.number || "").toLowerCase().includes(search.toLowerCase()) || (r.project_name || "").toLowerCase().includes(search.toLowerCase()) || (r.requested_by_name || "").toLowerCase().includes(search.toLowerCase()) || (r.items || []).some(i => (i.material_name || "").toLowerCase().includes(search.toLowerCase()));
  const matchUrgency = (r) => !filterUrgency || r.urgency === filterUrgency;
  const matchStatusSel = (r) => filterStatus === "todos" || r.status === filterStatus;
  const statusCounts = baseFiltered.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const byDateDesc = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
  const hasNewFilters = !!(search || filterUrgency || filterStatus !== "todos");
  // BLOQUE 50 PARTE B — pendientes (esta pestaña ya solo agrupa pendiente/modificar
  // arriba y aprobada/rechazada abajo mediante las 2 secciones); dentro de cada
  // grupo, el más nuevo primero.
  const pendingApprovals = baseFiltered.filter(r => ["pendiente", "modificar"].includes(r.status) && matchSearch(r) && matchUrgency(r) && matchStatusSel(r)).sort(byDateDesc);
  const recentActions = baseFiltered.filter(r => ["aprobada", "rechazada"].includes(r.status) && matchSearch(r) && matchUrgency(r) && matchStatusSel(r)).sort(byDateDesc).slice(0, hasNewFilters ? 100 : 10);

  const openEditModal = (req) => {
    setEditModal({ request: req });
    setEditForm({
      items: (req.items || []).map(i => ({ ...i })),
      urgency: req.urgency || "normal",
      target_date: req.target_date || "",
      sector: req.sector || "",
      notes: req.notes || "",
      comment: "",
    });
  };

  const updateEditItem = (idx, field, value) => {
    setEditForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: field === "quantity" ? (Number(value) || 1) : value };
      return { ...prev, items };
    });
  };

  const addEditItem = () => {
    // Fix DD (2026-09-16): nace con los mismos campos que una fila del formulario de
    // carga, si no el nivel y la tarea del ítem nuevo quedaban fuera del pedido.
    setEditForm(prev => ({ ...prev, items: [...prev.items, { material_name: "", quantity: 1, unit: "un", marca_recomendada: "", sector: "", front: "", categoria: "menores", item_uid: null, nivel_id: "", tarea_numero: "", observacion: "" }] }));
  };

  const removeEditItem = (idx) => {
    setEditForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const handleSaveEdit = async (approveAfter) => {
    if (!editModal) return;
    if (!editForm.items.some(i => i.material_name && i.material_name.trim())) {
      toast.error("Debe haber al menos un ítem con descripción");
      return;
    }
    setEditSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/purchases/requests/${editModal.request._id}/edit`,
        {
          items: editForm.items,
          urgency: editForm.urgency,
          target_date: editForm.target_date,
          sector: editForm.sector,
          notes: editForm.notes,
          comment: editForm.comment,
          approve_after: approveAfter,
        },
        { withCredentials: true }
      );
      toast.success(approveAfter ? `Pedido editado. ${mensajeAprobacion(data, editModal.request.number)}` : "Pedido editado");
      setEditModal(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al editar"); }
    finally { setEditSaving(false); }
  };

  const handleApprove = (req) => {
    // Show confirmation modal for approval (comment optional)
    setActionModal({ type: "aprob", request: req });
    setComment("");
  };

  // Fix AS (2026-09-05): "ahora mismo tampoco me deja rechazar el pedido"
  // (Edson, 05/09). El motivo es obligatorio y el botón quedaba deshabilitado
  // con el campo vacío: sin mensaje, parece que el sistema no responde. Ahora el
  // botón siempre se puede tocar y, si falta el motivo, lo dice y lleva al campo.
  const [faltaMotivo, setFaltaMotivo] = useState(false);

  const handleConfirmAction = async () => {
    if (!actionModal) return;
    if (actionModal.type !== "aprob" && !comment.trim()) {
      setFaltaMotivo(true);
      toast.error(actionModal.type === "rech"
        ? "Escribí el motivo del rechazo para poder rechazar el pedido"
        : "Escribí las instrucciones de modificación");
      document.querySelector('[data-testid="action-comment-input"]')?.focus();
      return;
    }
    setFaltaMotivo(false);
    setProcessing(actionModal.request._id);
    try {
      if (actionModal.type === "aprob") {
        const { data } = await axios.put(`${API}/purchases/requests/${actionModal.request._id}/approve`, { comment }, { withCredentials: true });
        toast.success(mensajeAprobacion(data, actionModal.request.number));
      } else {
        const endpoint = actionModal.type === "rech" ? "reject" : "modify";
        await axios.put(`${API}/purchases/requests/${actionModal.request._id}/${endpoint}`, { comment }, { withCredentials: true });
        toast.success(actionModal.type === "rech" ? "Pedido rechazado" : "Pedido devuelto para modificación");
      }
      setActionModal(null);
      setComment("");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setProcessing(null); }
  };

  return (
    <div className="space-y-6">
      {/* Filtro Cliente -> Obra -> Presupuesto (BLOQUE 27) + Buscar/Estado/Urgencia (BLOQUE 50) */}
      <div className="flex items-center gap-2 flex-wrap">
        <FiltroClienteObraPresupuesto
          listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets}
          filterCliente={filterCliente} setFilterCliente={setFilterCliente}
          filterObra={filterObra} setFilterObra={setFilterObra}
          filterPresupuesto={filterPresupuesto} setFilterPresupuesto={setFilterPresupuesto}
          testIdPrefix="aprobaciones"
        />
        <BusquedaEstadoUrgenciaFilters
          search={search} setSearch={setSearch}
          searchPlaceholder="Buscar por N°, obra o material..."
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          statusOptions={[
            { value: "todos", label: `Todos (${baseFiltered.length})` },
            { value: "pendiente", label: `Pendiente (${statusCounts.pendiente || 0})` },
            { value: "modificar", label: `A Modificar (${statusCounts.modificar || 0})` },
            { value: "aprobada", label: `Aprobada (${statusCounts.aprobada || 0})` },
            { value: "rechazada", label: `Rechazada (${statusCounts.rechazada || 0})` },
          ]}
          filterUrgency={filterUrgency} setFilterUrgency={setFilterUrgency}
          testIdPrefix="aprobaciones"
        />
        {(filterCliente || filterObra || filterPresupuesto || search || filterUrgency || filterStatus !== "todos") && (
          <button onClick={() => { setFilterCliente(""); setFilterObra(""); setFilterPresupuesto(""); setSearch(""); setFilterUrgency(""); setFilterStatus("todos"); }}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl px-2 py-2 hover:bg-gray-50 transition-all" title="Limpiar filtros"
            data-testid="aprobaciones-clear-filter">
            <X size={12} /> Limpiar
          </button>
        )}
      </div>

      {/* Pendientes de aprobación */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-bold text-gray-900">Pendientes de Aprobación</h3>
          {pendingApprovals.length > 0 && (
            <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">{pendingApprovals.length}</span>
          )}
        </div>

        {loading ? <Skeleton className="h-32" /> : pendingApprovals.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200/60 p-10 text-center">
            <CheckCircle2 size={36} className="mx-auto text-emerald-300 mb-2" />
            <p className="text-gray-500 text-sm font-medium">No hay pedidos pendientes de aprobación</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingApprovals.map(r => (
              <div key={r._id} className="bg-white rounded-xl border border-amber-200 shadow-sm p-4" data-testid="approval-card">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-gray-900 font-mono text-sm">{r.number}</p>
                      <StatusBadge status={r.status} />
                      {r.urgency === "alta" && <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">ALTA</span>}
                      {r.urgency === "critica" && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">CRÍTICO</span>}
                    </div>
                    <p className="text-xs text-gray-600 mt-1 font-medium">{r.project_name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{r.items?.length || 0} items · Solicitado por {r.requested_by_name} · {formatDate(r.created_at)}</p>
                    {r.target_date && <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Calendar size={10} /> Fecha objetivo: {formatDate(r.target_date)}</p>}
                    {r.modification_comment && (
                      <div className="mt-2 bg-orange-50 rounded-lg px-3 py-2 border border-orange-100">
                        <p className="text-xs text-orange-700">Motivo anterior: {r.modification_comment}</p>
                      </div>
                    )}
                    <AprobacionParcial parciales={r.aprobaciones_parciales} testId={`aprobacion-parcial-${r.number}`} />
                    {/* Items preview */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(r.items || []).slice(0, 4).map((item, i) => (
                        <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-lg">
                          {item.material_name} × {item.quantity} {item.unit}
                        </span>
                      ))}
                      {(r.items || []).length > 4 && <span className="text-[10px] text-gray-400">+{r.items.length - 4} más</span>}
                    </div>
                  </div>

                  {(canApprove || aprobadorPerfil) && (
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(r)}
                          disabled={!!processing}
                          className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-60"
                          data-testid="aprob-btn">
                          {processing === r._id + "_aprob" ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                          APROB
                        </button>
                        {canApprove && (<>
                        <button
                          onClick={() => openEditModal(r)}
                          className="flex items-center gap-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-2 rounded-xl text-xs font-bold transition-all"
                          data-testid="edit-pedido-btn"
                          title="Editar pedido directamente">
                          <Pencil size={13} /> EDITAR
                        </button>
                        <button
                          onClick={() => { setActionModal({ type: "mod", request: r }); setComment(""); }}
                          className="flex items-center gap-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-2 rounded-xl text-xs font-bold transition-all"
                          data-testid="mod-btn"
                          title="Devolver para que el solicitante modifique">
                          <RotateCcw size={13} /> DEVOLVER
                        </button>
                        <button
                          onClick={() => { setActionModal({ type: "rech", request: r }); setComment(""); }}
                          className="flex items-center gap-1.5 bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded-xl text-xs font-bold transition-all"
                          data-testid="rech-btn">
                          <X size={13} /> RECH
                        </button>
                        </>)}
                      </div>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setViewRequest(r)}
                          className="flex items-center gap-1.5 border border-[#2E6DA4]/30 text-[#2E6DA4] hover:bg-blue-50 px-3 py-1.5 rounded-xl text-[11px] font-semibold transition-all"
                          data-testid="ver-approval-btn">
                          <Eye size={12} /> VER
                        </button>
                        <button onClick={() => descargarPedidoPDF(r)}
                          className="flex items-center gap-1.5 border border-gray-200 text-gray-500 hover:bg-gray-50 px-3 py-1.5 rounded-xl text-[11px] font-semibold transition-all"
                          data-testid="pdf-approval-btn">
                          <Download size={12} /> PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Acciones recientes */}
      {recentActions.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-3">Acciones Recientes</h3>
          <div className="space-y-2">
            {recentActions.map(r => (
              <div key={r._id} className="flex items-center justify-between bg-white rounded-xl border border-gray-200/60 px-4 py-3" data-testid="recent-action-card">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-800 font-mono">{r.number}</p>
                    {r.apm_number && <span className="text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded font-mono font-bold" data-testid="apm-badge">{r.apm_number}</span>}
                  </div>
                  <p className="text-xs text-gray-500">{r.project_name} · {r.requested_by_name}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={r.status} />
                  {r.approved_by_name && <p className="text-xs text-gray-400 mt-1">Por: {r.approved_by_name}</p>}
                  {r.rejected_by_name && <p className="text-xs text-gray-400 mt-1">Por: {r.rejected_by_name}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal APROB/RECH/MOD con comentario */}
      <Modal open={!!actionModal} onClose={() => setActionModal(null)}
        title={actionModal?.type === "aprob" ? "Confirmar Aprobación" : actionModal?.type === "rech" ? "Rechazar Pedido" : "Devolver para Modificación"}>
        {actionModal && (
          <div className="space-y-4">
            <div className={`rounded-xl p-4 border ${actionModal.type === "aprob" ? "bg-emerald-50 border-emerald-200" : actionModal.type === "rech" ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"}`}>
              <p className={`text-sm font-semibold ${actionModal.type === "aprob" ? "text-emerald-800" : actionModal.type === "rech" ? "text-red-800" : "text-orange-800"}`}>
                Pedido: {actionModal.request.number}
              </p>
              <p className="text-xs text-gray-600 mt-1">{actionModal.request.project_name} · {actionModal.request.items?.length || 0} items</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                {actionModal.type === "aprob" ? "Comentario (opcional)" : actionModal.type === "rech" ? "Motivo de rechazo *" : "Instrucciones de modificación *"}
              </label>
              <textarea
                value={comment}
                onChange={e => { setComment(e.target.value); if (e.target.value.trim()) setFaltaMotivo(false); }}
                rows={3}
                placeholder={actionModal.type === "aprob" ? "Comentario de aprobación (opcional)..." : actionModal.type === "rech" ? "Indique el motivo del rechazo..." : "Indique qué debe modificarse..."}
                className={`w-full border rounded-xl px-3 py-2.5 text-sm resize-none focus:ring-2 focus:ring-[#2E6DA4]/20 ${faltaMotivo ? "border-red-400 bg-red-50/40" : "border-gray-300"}`}
                data-testid="action-comment-input"
              />
              {/* Fix AS (2026-09-05): por qué no avanza, en vez de un botón apagado. */}
              {actionModal.type !== "aprob" && (
                <p className={`text-[11px] mt-1 ${faltaMotivo ? "text-red-600 font-semibold" : "text-gray-400"}`} data-testid="action-comment-hint">
                  {actionModal.type === "rech" ? "El motivo es obligatorio: queda en el historial del pedido." : "Las instrucciones son obligatorias: las ve quien cargó el pedido."}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setActionModal(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button
                onClick={handleConfirmAction}
                disabled={!!processing}
                className={`flex-1 text-white rounded-xl py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60 transition-all ${
                  actionModal.type === "aprob" ? "bg-emerald-500 hover:bg-emerald-600" : actionModal.type === "rech" ? "bg-red-500 hover:bg-red-600" : "bg-orange-500 hover:bg-orange-600"
                }`}
                data-testid="confirm-action-btn">
                {processing ? <Loader2 size={14} className="animate-spin" /> : null}
                {actionModal.type === "aprob" ? "Confirmar Aprobación" : actionModal.type === "rech" ? "Confirmar Rechazo" : "Enviar para Modificación"}
              </button>
            </div>
          </div>
        )}
      </Modal>
      <PedidoDetailModal open={!!viewRequest} onClose={() => setViewRequest(null)} request={viewRequest} />

      {/* Modal: Editar Pedido directamente */}
      <Modal open={!!editModal} onClose={() => !editSaving && setEditModal(null)} title={editModal ? `Editar Pedido ${editModal.request.number}` : ""} extraWide>
        {editModal && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs text-blue-800 font-semibold">{editModal.request.project_name}</p>
              <p className="text-[11px] text-blue-700 mt-0.5">Solicitado por {editModal.request.requested_by_name} · {editModal.request.items?.length || 0} ítems originales</p>
              {/* Fix ED (2026-09-24): presupuesto de referencia (código y nombre) y cliente. */}
              <p className="text-[11px] text-blue-700 mt-0.5" data-testid="edit-presupuesto-ref">
                Presupuesto de referencia: <strong>{textoPresupuestoRef(presupuestoRef(editModal.request, editPtoInfo))}</strong>
                {" · "}Cliente: <strong>{presupuestoRef(editModal.request, editPtoInfo).cliente || "—"}</strong>
              </p>
            </div>
            {/* Fix AS (2026-09-05): sin imputar, el consumo de la obra no cuenta el material. */}
            {editItemsPto.length > 0 && editForm.items.some(i => !i.item_uid) && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="edit-aviso-sin-imputar">
                {editForm.items.filter(i => !i.item_uid).length} ítem(s) sin imputar al presupuesto. Elegí la línea en "Ítem del Pto" para que el consumo de la obra los cuente y aparezca el análisis.
              </p>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Urgencia</label>
                <select value={editForm.urgency} onChange={e => setEditForm(p => ({ ...p, urgency: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs bg-white"
                  data-testid="edit-urgency">
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="critica">Crítico</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha objetivo</label>
                <input type="date" value={editForm.target_date} onChange={e => setEditForm(p => ({ ...p, target_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs"
                  data-testid="edit-target-date" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sector</label>
                <input value={editForm.sector} onChange={e => setEditForm(p => ({ ...p, sector: e.target.value }))}
                  placeholder="Sector / piso"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs"
                  data-testid="edit-sector" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-700">Ítems del pedido</label>
                <button type="button" onClick={addEditItem}
                  className="flex items-center gap-1 text-xs text-[#2E6DA4] hover:text-[#245A8A] font-semibold"
                  data-testid="edit-add-item-btn">
                  <Plus size={12} /> Agregar ítem
                </button>
              </div>
              <div className="space-y-2">
                {/* Fix AS (2026-09-05): categoría e ítem del presupuesto, para que
                    el aprobador pueda imputar lo que el residente no imputó. */}
                {/* Fix DD (2026-09-16): las mismas columnas que el formulario de carga.
                    Faltaban Nivel, Tarea y Observación: el aprobador veía esos datos en el
                    detalle del pedido pero no podía corregirlos acá. */}
                <div className="grid gap-1 text-[10px] font-semibold text-gray-400 uppercase px-1" style={{ gridTemplateColumns: "24px 92px 2.2fr 50px 56px 1.7fr 78px 104px 104px 1fr 1.1fr 24px" }}>
                  <span>#</span>
                  <span>Categ.</span>
                  <span>Descripción</span>
                  <span>UM</span>
                  <span>Cant.</span>
                  <span>Ítem del Pto</span>
                  <span>Sector</span>
                  <span>Nivel</span>
                  <span>Tarea</span>
                  <span>Marca</span>
                  <span>Observación</span>
                  <span></span>
                </div>
                {editForm.items.map((item, i) => (
                  <div key={i} className="grid gap-1 items-center bg-gray-50 rounded-lg p-2" style={{ gridTemplateColumns: "24px 92px 2.2fr 50px 56px 1.7fr 78px 104px 104px 1fr 1.1fr 24px" }}>
                    <span className="text-[11px] font-bold text-[#2E6DA4] bg-blue-100 rounded-full w-6 h-6 flex items-center justify-center">{i + 1}</span>
                    {/* Fix AS (2026-09-05): categoría del ítem. "Del listado" exige
                        elegir la línea del presupuesto; las otras no se imputan. */}
                    <select value={item.categoria || "menores"}
                      onChange={e => {
                        const v = e.target.value;
                        updateEditItem(i, "categoria", v);
                        if (v !== "listado") updateEditItem(i, "item_uid", null);
                      }}
                      className="border border-gray-300 rounded-lg px-1 py-1.5 text-[11px] bg-white"
                      data-testid={`edit-item-categoria-${i}`}>
                      {editItemsPto.length > 0 && <option value="listado">Del listado del Pto</option>}
                      <option value="menores">Material menor</option>
                      <option value="imprevistos">Imprevisto</option>
                      <option value="equipamientos">Equipamiento</option>
                      <option value="herramientas">Herramienta</option>
                    </select>
                    {/* Fix IU (2026-11-02): la descripción se ve completa y se sigue editando. */}
                    <AutocompleteInput value={item.material_name || ""} onChange={v => updateEditItem(i, "material_name", v)}
                      placeholder="Descripción" multiline className="min-w-[210px]"   /* el mismo ancho mínimo que tenía el campo de una línea */
                      inputClassName="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs"
                      data-testid={`edit-item-name-${i}`} />
                    <select value={item.unit || "un"} onChange={e => updateEditItem(i, "unit", e.target.value)}
                      className="border border-gray-300 rounded-lg px-1.5 py-1.5 text-xs bg-white">
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                    <input type="number" min="1" value={item.quantity || 1} onChange={e => updateEditItem(i, "quantity", e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs text-center"
                      data-testid={`edit-item-qty-${i}`} />
                    {/* Fix AS (2026-09-05): línea del presupuesto a la que se imputa,
                        agrupada por sección del cómputo (PLANTA BAJA, AZOTEA...). */}
                    <select value={item.item_uid || ""}
                      onChange={e => {
                        const v = e.target.value || null;
                        updateEditItem(i, "item_uid", v);
                        if (v) updateEditItem(i, "categoria", "listado");
                      }}
                      disabled={editItemsPto.length === 0}
                      className={`border rounded-lg px-1 py-1.5 text-[11px] bg-white disabled:bg-gray-100 ${item.categoria === "listado" && !item.item_uid ? "border-amber-300 bg-amber-50/40" : "border-gray-300"}`}
                      data-testid={`edit-item-ptoitem-${i}`}>
                      <option value="">{editItemsPto.length > 0 ? "— Sin imputar —" : "Sin presupuesto"}</option>
                      {Object.entries(editItemsPto.reduce((acc, bi) => {
                        const k = [bi.seccion_numero, bi.seccion].filter(Boolean).join("  ") || "Sin sección";
                        (acc[k] = acc[k] || []).push(bi);
                        return acc;
                      }, {})).map(([seccion, lineas]) => (
                        <optgroup key={seccion} label={seccion}>
                          {lineas.map(bi => (
                            <option key={bi.item_uid} value={bi.item_uid}>{`${bi.numero ? bi.numero + " " : ""}${bi.descripcion}`}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <input value={item.sector || ""} onChange={e => updateEditItem(i, "sector", e.target.value)}
                      placeholder="Sector"
                      className="border border-gray-300 rounded-lg px-1.5 py-1.5 text-xs" />
                    {/* Fix DD (2026-09-16): Nivel como desplegable, con los niveles del
                        presupuesto vinculado. Es el mismo dato y el mismo formato que en el
                        formulario de carga, para que el aprobador pueda completarlo o
                        corregirlo antes de aprobar. */}
                    <select value={item.nivel_id || ""} onChange={e => updateEditItem(i, "nivel_id", e.target.value)}
                      disabled={editNiveles.length === 0}
                      className="border border-gray-300 rounded-lg px-1 py-1.5 text-[11px] bg-white disabled:bg-gray-100"
                      data-testid={`edit-item-nivel-${i}`}>
                      <option value="">{editNiveles.length > 0 ? "— Nivel —" : "Sin niveles"}</option>
                      {editNiveles.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
                    </select>
                    {/* Fix DD (2026-09-16): primero las tareas del ítem elegido, después el
                        catálogo completo — igual que en el formulario de carga. */}
                    <select value={item.tarea_numero || ""} onChange={e => updateEditItem(i, "tarea_numero", e.target.value)}
                      className="border border-gray-300 rounded-lg px-1 py-1.5 text-[11px] bg-white"
                      data-testid={`edit-item-tarea-${i}`}>
                      <option value="">— Tarea —</option>
                      {(editTareas.porItem[item.item_uid] || []).length > 0 && (
                        <optgroup label="Del ítem">
                          {(editTareas.porItem[item.item_uid] || []).map(n => (
                            <option key={`i-${n}`} value={n}>{`${n}. ${editTareas.nombres[n] || ""}`}</option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label={(editTareas.porItem[item.item_uid] || []).length > 0 ? "Todas" : "Catálogo"}>
                        {editTareas.todas.map(t => (
                          <option key={t.numero} value={t.numero}>{`${t.numero}. ${t.nombre}`}</option>
                        ))}
                      </optgroup>
                    </select>
                    <input value={item.marca_recomendada || ""} onChange={e => updateEditItem(i, "marca_recomendada", e.target.value)}
                      placeholder="Marca"
                      className="border border-gray-300 rounded-lg px-1.5 py-1.5 text-xs" />
                    <input value={item.observacion || ""} onChange={e => updateEditItem(i, "observacion", e.target.value)}
                      placeholder="Observación"
                      className="border border-gray-300 rounded-lg px-1.5 py-1.5 text-xs"
                      data-testid={`edit-item-observacion-${i}`} />
                    {editForm.items.length > 1 ? (
                      <button type="button" onClick={() => removeEditItem(i)} className="flex justify-center text-red-400 hover:text-red-600">
                        <X size={14} />
                      </button>
                    ) : <div />}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones / motivo del cambio</label>
              <textarea value={editForm.comment} onChange={e => setEditForm(p => ({ ...p, comment: e.target.value }))}
                rows={2} placeholder="Indique brevemente qué se cambió y por qué..."
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs resize-none"
                data-testid="edit-comment" />
            </div>

            <div className="flex gap-2 pt-2 border-t border-gray-100">
              <button onClick={() => setEditModal(null)} disabled={editSaving}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">Cancelar</button>
              <button onClick={() => handleSaveEdit(false)} disabled={editSaving}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-xl py-2.5 text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="save-edit-btn">
                {editSaving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                Guardar cambios
              </button>
              <button onClick={() => handleSaveEdit(true)} disabled={editSaving}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl py-2.5 text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="save-and-approve-btn">
                {editSaving ? <Loader2 size={13} className="animate-spin" /> : <CheckCheck size={13} />}
                Guardar y APROBAR
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
